import React, { useEffect } from "react";
import { Space, Card, Row, Col, Radio, InputNumber, Form, Typography } from "antd";
import { TypeSelector } from "./TypeSelector";
import { ScopeSelector } from "./ScopeSelector";
import { SourcingType, SourcingScope, TechnicalEvaluationMode } from "../interface";

const { Text } = Typography;

interface TypeScopeSelectorProps {
  typeValue?: SourcingType;
  scopeValue?: SourcingScope;
  technicalEvaluationMode?: TechnicalEvaluationMode;
  technicalEvaluationThreshold?: number;
  onTypeChange?: (value: SourcingType) => void;
  onScopeChange?: (value: SourcingScope) => void;
  onTechnicalEvaluationModeChange?: (value: TechnicalEvaluationMode) => void;
  onTechnicalEvaluationThresholdChange?: (value: number | undefined) => void;
}

export const TypeScopeSelector: React.FC<TypeScopeSelectorProps> = ({
  typeValue,
  scopeValue,
  technicalEvaluationMode,
  technicalEvaluationThreshold,
  onTypeChange,
  onScopeChange,
  onTechnicalEvaluationModeChange,
  onTechnicalEvaluationThresholdChange
}) => {
  // Set technical evaluation mode based on sourcing type
  useEffect(() => {
    if (typeValue === SourcingType.RFP) {
      // RFP must use MATRIX mode
      if (technicalEvaluationMode !== TechnicalEvaluationMode.MATRIX) {
        onTechnicalEvaluationModeChange?.(TechnicalEvaluationMode.MATRIX);
      }
    } else if (typeValue === SourcingType.RFQ) {
      // RFQ can choose any mode, default to NONE if not set
      if (!technicalEvaluationMode) {
        onTechnicalEvaluationModeChange?.(TechnicalEvaluationMode.NONE);
      }
    }
  }, [typeValue, technicalEvaluationMode, onTechnicalEvaluationModeChange]);

  const handleModeChange = (e: any) => {
    const newMode = e.target.value;
    onTechnicalEvaluationModeChange?.(newMode);
    // Clear threshold if not MATRIX
    if (newMode !== TechnicalEvaluationMode.MATRIX) {
      onTechnicalEvaluationThresholdChange?.(undefined);
    }
  };

  return (
    <Row gutter={16}>
      <Col span={12}>
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          <Card title="Sourcing Type" size="small">
            <TypeSelector
              value={typeValue}
              onChange={onTypeChange}
            />
          </Card>

          <Card title="Sourcing Scope" size="small">
            <ScopeSelector
              value={scopeValue}
              onChange={onScopeChange}
            />
          </Card>
        </Space>
      </Col>
      <Col span={12}>
        <Card title="Technical Evaluation" size="small">
          <Space direction="vertical" size="middle" style={{ width: "100%" }}>
            <div>
              <Text strong>Technical Evaluation Mode</Text>
              <Form.Item
                help={
                  <div>
                    <Text type="secondary">NONE: No technical evaluation required</Text><br />
                    <Text type="secondary">PASS_FAIL: Simple pass/fail criteria</Text><br />
                    <Text type="secondary">MATRIX: Weighted scoring matrix</Text>
                  </div>
                }
              >
                <Radio.Group
                  value={technicalEvaluationMode}
                  onChange={handleModeChange}
                  style={{ marginTop: 8 }}
                  disabled={typeValue === SourcingType.RFP}
                >
                  <Radio value={TechnicalEvaluationMode.NONE} disabled={typeValue === SourcingType.RFP}>None</Radio>
                  <Radio value={TechnicalEvaluationMode.PASS_FAIL} disabled={typeValue === SourcingType.RFP}>Pass/Fail</Radio>
                  <Radio value={TechnicalEvaluationMode.MATRIX} disabled={typeValue === SourcingType.RFP}>Matrix</Radio>
                </Radio.Group>
              </Form.Item>
            </div>

            {technicalEvaluationMode === TechnicalEvaluationMode.MATRIX && (
              <div>
                <Text strong>Technical Evaluation Threshold (%)</Text>
                <Form.Item
                  help="Minimum score required for qualification (0-100%)"
                  rules={[
                    { required: true, message: 'Threshold is required for Matrix mode' },
                    { type: 'number', min: 0, max: 100, message: 'Threshold must be between 0 and 100' }
                  ]}
                >
                  <InputNumber
                    value={technicalEvaluationThreshold}
                    onChange={(value) => onTechnicalEvaluationThresholdChange?.(value || 0)}
                    placeholder="Enter minimum score required"
                    min={0}
                    max={100}
                    style={{ width: '100%', marginTop: 8 }}
                    addonAfter="%"
                  />
                </Form.Item>
              </div>
            )}
          </Space>
        </Card>
      </Col>
    </Row>
  );
};